package ui.resource.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.text.input.TextFieldValue
import api.ResourceType
import api.VisualProperties
import common.frontend.api.Io
import org.ssoft.common.type.ResolutionStandard
import provider.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.globalViewModel
import tools.*


interface VisualEditorScope {

    val vm: VisualEditorVM
    val action: (VisualEditorVM.Action) -> Unit
    val account: CommonAccountProvider
    val display: CommonDisplayProvider
    val externals: CommonExternalsProvider

    val args: VisualEditorArgs
    val nav: Navigation
    val login: Io.User

    companion object {
        @Composable
        fun rememberScope(): VisualEditorScope {
            val nav = LocalNavigation.current
            val account = LocalAccountProvider.current
            val display = LocalDisplayProvider.current
            val externals = LocalExternalsProvider.current

            val args = LocalNavigationArgs.current
                .rememberArgs(VisualEditorArgs::class)
            val vm = globalViewModel(VisualEditorVM::class, "${args.companyId}:${args.resourceId}")
            val action = VisualEditorVM.Action.rememberAction(vm)

            return remember {
                object : VisualEditorScope {
                    override val vm: VisualEditorVM
                        get() = vm
                    override val action: (VisualEditorVM.Action) -> Unit
                        get() = action
                    override val account: CommonAccountProvider
                        get() = account
                    override val display: CommonDisplayProvider
                        get() = display
                    override val externals: CommonExternalsProvider
                        get() = externals
                    override val args: VisualEditorArgs
                        get() = args
                    override val nav: Navigation
                        get() = nav

                    override val login: Io.User
                        get() = account.current!!
                }.also {
                    vm.init(it)
                }
            }
        }
    }


    class Wrapper(
        val canUserDeleteResource: Boolean,
        val containsReferences: Boolean,
        val title: Pair<String, String>,
        val data: Io.Resource,
        val type: ResourceType = ResourceType[data.type]
    ) {

        private val _changed = mutableStateOf(false)
        val changed: State<Boolean> get() = _changed


        val visualProperties = VisualProperties(
            type = data.type,
            name = data.name,
            widthIn = data.width,
            heightIn = data.height,
            durationMsIn = data.duration,
            deleteOrigin = canUserDeleteResource && !containsReferences
        )

        val origin = visualProperties.copy()

        private fun makeChanges() {
            _changed.value = !propW.err &&
                !propW.err &&
                !propW.err &&
                !propName.err &&
                (!propDur.err || ResourceType.IMAGE match visualProperties.type) &&
                visualProperties.changed()
        }

        val propName = EFieldTextString(
            property = visualProperties::name,
            condition = { it.trim().takeIf { it.isNotEmpty() } },
            onChange = { makeChanges() })

        val propW = EFieldTextInt(
            property = visualProperties::widthOut,
            condition = { it.toIntOrNull()?.takeIf { it in 1..3840 } },
            onChange = { makeChanges() })

        val propH = EFieldTextInt(
            property = visualProperties::heightOut,
            condition = { it.toIntOrNull()?.takeIf { it in 1..3840 } },
            onChange = { makeChanges() })

        val propS = EFieldRegularInt(
            property = visualProperties::scale,
            onChange = { makeChanges() })

        val propDur = EFieldTextMsAsSec(
            property = visualProperties::durationMsOut,
            condition = { it.toLongOrNull()?.takeIf { it > 1 } },
            onChange = { makeChanges() })

        val propDelOrigin = EFieldRegularBoolean(
            property = visualProperties::deleteOrigin
        )

        fun switchOrientation() {
            val w = propW.state
            propW.set(TextFieldValue(propH.state.toString()))
            propH.set(TextFieldValue(w.toString()))
        }

        fun setResolution(it: ResolutionStandard) {
            if (data.width > data.height) {
                propW.set(TextFieldValue(it.width.toString()))
                propH.set(TextFieldValue(it.height.toString()))
            } else {
                propW.set(TextFieldValue(it.height.toString()))
                propH.set(TextFieldValue(it.width.toString()))
            }
        }
    }
}

